/**
 * SpaNavManager manages navigation of reports as a single page application.
 * i.e user feels like new page was loaded, but in reality report was swapped without a page refresh.
 * NOTE: This currently only works for iron apps.
 */

import * as ReactDOM from 'react-dom/client';

import {AdditionalContext, QueryContext} from 'common/query-context';
import {viewedReportFlagManager} from 'common/report/queries/app-api/user-flags';
import {trackVisitedReport} from 'common/util/server-tracking';
import {VisitedReportReason} from 'common/util/server-tracking';
import {updateHelpCenterFromUrl} from 'common/report/external-widgets/zendesk';
import {clearAllToasts} from 'common/components/toast-area/toast-manager';
import {createElem} from 'common/util/dom';
import {apiClient} from 'common/util/api-client';
import {diffFromNow, getNow} from 'common/util/timing';
import {currentProjectStore} from 'common/report/stores/project-details';
import type {ReportEditor} from 'common/report-editor';
import {reporter} from 'common/report/error-reporting';
import {hasCleanerUrlsEnabled} from 'common/report/util/bookmark-urls';

import {waitUntilUiReady} from '../perf-measure';
import {ReportName} from '../constants';
import {layoutManager} from './layout-manager';
import {getReportName, IronAppInfo, urlToIronAppInfo} from './index';
import {parseStateFromUrl, UrlParsedState} from './state-from-url';

function preloadAsset(assetUrl: string, assetType: `style` | `script`): void {
  if (document.querySelector(`link[rel=preload][as=${assetType}][href="${assetUrl}"]`)) {
    return;
  }

  const link = document.createElement(`link`);
  link.setAttribute(`rel`, `preload`);
  link.setAttribute(`href`, assetUrl);
  link.setAttribute(`as`, assetType);
  link.setAttribute(`importance`, `low`);

  const head = document.querySelector(`head`);
  head.appendChild(link);
}

export type InitAppFunc = (
  options?: {container?: HTMLElement; onMount?: () => void; queryContext?: QueryContext} & Obj,
) => HTMLElement | ReactDOM.Root;

export interface AppAssets {
  js: string[];
  css: string[];
}

export interface AppAssetsUrls {
  [app: string]: AppAssets;
}

export interface InitOptions {
  projectId: string;
  projectName: string;
}

// api/app/asset-manifest/iron-apps is handled specially by webpack/dev-server-middleware.js for localhost
const locationOrigin = window.location.hostname === `localhost` ? window.location.origin : ``;
const appAssetsApiUrl = `${locationOrigin}/api/app/asset-manifest/iron-apps`;

export enum SpaNavStatus {
  Initialized = `initialized`,
  Initializing = `initializing`,
  Uninitialized = `uninitialized`,
}

export class SpaNavManager {
  private initAppFuncsMap: Map<string, InitAppFunc> = new Map();
  private appAssetsUrlMap: Map<string, AppAssets> = new Map();
  private preloadedAssets: Set<string> = new Set();
  private initOptions: InitOptions;
  public initializationStatus = SpaNavStatus.Uninitialized;
  public reactRoot: ReactDOM.Root;

  /** iron apps call into this via init-app.ts when initializing */
  setInitAppFn(appName: string, initAppFunc: InitAppFunc) {
    this.initAppFuncsMap.set(appName, initAppFunc);
  }

  async initialize(initOptions: InitOptions) {
    if ([SpaNavStatus.Initialized, SpaNavStatus.Initializing].includes(this.initializationStatus)) {
      return;
    }
    this.initializationStatus = SpaNavStatus.Initializing;

    this.initOptions = initOptions;
    const appAssetUrls = await this.fetchAppAssetUrls();
    this.setAppAssetsUrls(appAssetUrls);
    this.initializationStatus = SpaNavStatus.Initialized;
  }

  setAppAssetsUrls(appsAssetUrls: AppAssetsUrls) {
    if (appsAssetUrls) {
      this.preloadedAssets.clear();
      this.appAssetsUrlMap = new Map(Object.entries(appsAssetUrls));
    }
  }

  fetchAppAssetUrls() {
    return apiClient.get<AppAssetsUrls>(appAssetsApiUrl);
  }

  getIronAppInfo(urlState: UrlParsedState): IronAppInfo {
    let appInfo = null;
    const appNames = new Set(this.appAssetsUrlMap.keys());
    if (appNames.size) {
      const appName = urlState?.appName;
      appInfo = urlToIronAppInfo[appName];
      if (!appInfo || !appNames.has(appInfo.appName)) {
        appInfo = null;
      }
    }
    return appInfo;
  }

  isNavigable(nextUrlPath: string, curUrlPath): boolean {
    const curUrlState = parseStateFromUrl(curUrlPath);
    const nextUrlState = parseStateFromUrl(nextUrlPath);
    const curApp = this.getIronAppInfo(curUrlState);
    const nextApp = this.getIronAppInfo(nextUrlState);

    // If you try to go to the current app, the actual behavior is to refresh the page.
    // If `appName` variable is the same, we don't have to ask to user with a "confirm" dialog.
    return Boolean(
      curApp &&
        nextApp &&
        curApp.appName !== nextApp.appName &&
        curUrlState.projectId === nextUrlState.projectId &&
        curUrlState.workspaceId === nextUrlState.workspaceId,
    );
  }

  /**
   * Preload the app assets to try and cutdown on the network time (about 100ms) as users do quick navigation
   * within mixpanel. It will not trigger parsing, this is only meant to try and optimize the network
   */
  preloadAppAssets(newUrlPath: string) {
    // no known assets, so we can't possibly preload them, return early
    if (this.appAssetsUrlMap.size === 0) {
      return;
    }

    const newApp = this.getIronAppInfo(parseStateFromUrl(newUrlPath));
    const hasAlreadyLoaded = this.initAppFuncsMap.has(newApp.appName) || this.preloadedAssets.has(newApp.appName);

    if (!hasAlreadyLoaded) {
      const appAssetsUrlMap = this.appAssetsUrlMap;
      preloadAsset(`${appAssetsUrlMap.get(newApp.appName).js[0]}`, `script`);
      preloadAsset(`${appAssetsUrlMap.get(newApp.appName).css[0]}`, `style`);
      this.preloadedAssets.add(newApp.appName);
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  async navigate(
    newUrlPath: string,
    curUrlPath: string,
    ev: Event | null,
    keepReportContext = false,
    additionalContext: AdditionalContext = {},
  ) {
    const startTime = getNow();
    const curApp = this.getIronAppInfo(parseStateFromUrl(curUrlPath));
    const newApp = this.getIronAppInfo(parseStateFromUrl(newUrlPath));

    if (this.initOptions.projectId) {
      viewedReportFlagManager.increment(); // async, no need to wait
    }

    if (!curApp || !newApp || curApp.appName === newApp.appName) {
      // nothing to do, let the app's built-in router handle the navigation or let the browser refresh the page
      return;
    }

    if (ev && ev.type === `click`) {
      // don't reload the page with new url, we'll do a spa navigate
      ev.preventDefault();
    }

    incrementSpaNavCount();

    const $mixpanelApp = document.querySelector(`#mixpanel-application`);

    let queryContext: QueryContext;
    // TODO: remove this part, query context should be fully managed by context
    if (keepReportContext && hasCleanerUrlsEnabled(currentProjectStore.value)) {
      const $reportEditor: ReportEditor = $mixpanelApp.querySelector(`mp-report-editor`);
      if ($reportEditor) {
        queryContext = $reportEditor.getReportQueryContext({additionalContext});
      } else {
        reporter.error(`SpaNavManager.nav: Could not find <mp-report-editor> to retain QueryContext`, {
          curUrlPath,
          newUrlPath,
        });
      }
    }

    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    } else {
      // innerHTML = ``  will disconnect existing top level app component
      // and browser will recursively call disconnectedCallback for the component tree
      $mixpanelApp.innerHTML = ``;
    }
    // e.g MusicFinder / Jql Console
    document.title = `${this.initOptions.projectName} / ${newApp.title}`;

    layoutManager.reset();

    // change the url without re-loading to the new app's url
    if (ev && ev.type !== `popstate`) {
      // if we push on a popstate event, we break browser back button navigation, don't do that
      window.history.pushState(``, ``, newUrlPath);
    }

    // disable old css and add new css if not already loaded
    // if new css is already there, just activate it
    const appAssetsUrlMap = this.appAssetsUrlMap;
    const $curAppCss: any = document.querySelector(
      `head > link[href='${appAssetsUrlMap.get(curApp.appName).css[0]}'][rel='stylesheet']`,
    );
    const $newAppCss: any = document.querySelector(
      `head > link[href='${appAssetsUrlMap.get(newApp.appName).css[0]}'][rel='stylesheet']`,
    );
    if ($curAppCss) {
      $curAppCss.disabled = true; // see https://guides.codechewing.com/js/disable-enable-stylesheet-javascript
    }
    if ($newAppCss) {
      $newAppCss.disabled = false;
    } else {
      document.head.appendChild(
        createElem(`link`, {rel: `stylesheet`, href: `${appAssetsUrlMap.get(newApp.appName).css[0]}`}),
      );
    }

    // add new js script if not already loaded
    // otherwise re-initialize existing app via it's initAppFunc
    if (!this.initAppFuncsMap.has(newApp.appName)) {
      document.querySelector(`.whiteout_loader`)?.classList.remove(`hidden`);
      document.body.appendChild(
        createElem(`script`, {
          type: `text/javascript`,
          src: `${appAssetsUrlMap.get(newApp.appName).js[0]}`,
        }),
      );
    } else {
      const initAppFunc = this.initAppFuncsMap.get(newApp.appName);
      const appElem = initAppFunc({container: $mixpanelApp as HTMLElement, queryContext});
      if (appElem instanceof HTMLElement) {
        $mixpanelApp.appendChild(appElem);
      } else {
        this.reactRoot = appElem;
      }
    }

    // wait for report shell to load and track report loaded events
    await waitUntilUiReady();
    document.querySelector(`.whiteout_loader`)?.classList.add(`hidden`);
    const elapsedMs = diffFromNow(startTime);

    // update zendesk widget suggestions
    updateHelpCenterFromUrl();

    clearAllToasts();

    this._trackReportLoaded(getReportName(newApp.appName), elapsedMs, keepReportContext);
  }

  /**
   * track 'Viewed Report' and 'Report Loaded' events with 'is_spa_nav' flag so we can pretend a pageload occurred
   */
  private _trackReportLoaded(report: ReportName, elapsedMs: number, keepReportContext: boolean) {
    const projectId = this.initOptions.projectId;
    const commonTrackProps = {
      project_id: projectId,
      is_spa_nav: true,
      tab: report,
      keptReportContext: keepReportContext,
    };

    mixpanel.track(`Viewed report`, commonTrackProps);

    trackVisitedReport({
      projectId,
      reportName: report,
      reason: VisitedReportReason.ReportVoltNavigate,
    });

    mixpanel.track(`Report Loaded`, {
      ...commonTrackProps,
      preloadedAssets: this.preloadedAssets.has(report),
      page_time_to_ui_ready_ms: elapsedMs,
    });

    mixpanel.infra_metrics.track(`report-loaded`, {
      ...commonTrackProps,
      distinct_id: projectId,
      preloadedAssets: this.preloadedAssets.has(report),
      page_time_to_ui_ready_ms: elapsedMs,
    });
  }
}

function incrementSpaNavCount() {
  window[`__spaNavCount`] = (window[`__spaNavCount`] ?? 0) + 1;
}

export function getSpaNavCount() {
  return window[`__spaNavCount`] ?? 0;
}

// window singleton
export const spaNavManager: SpaNavManager = (window[`__spaNavManager`] =
  window[`__spaNavManager`] || new SpaNavManager());
